import { Box, Button, CircularProgress, Alert, Badge } from '@mui/material'
import { backgroundColors } from '../Components/Common/Color/Color'
import { useEffect, useState, useCallback, useMemo, Suspense, lazy } from 'react'
import FilterListIcon from '@mui/icons-material/FilterList'
import RefreshIcon from '@mui/icons-material/Refresh'
import { useAppDispatch, useAppSelector } from '../redux/store'
import { fetchTeamMembers } from '../redux/teamMember/actions'
import TeamRosterFilter from '../Components/TeamRoster/Filter'
import Upload from '../Components/TeamRoster/Upload'
import SearchInput from '../Components/Common/SearchInput'
import LastUpdateInfo from '../Components/TeamRoster/LastUpdateInfo'
import { 
  selectSearchQuery, 
  selectLoadingTeamMembers, 
  selectErrorTeamMembers,
  selectTeamMembers,
  selectActiveFilterCount,
} from '../redux/teamMember/selector'
import { setSearchQuery } from '../redux/teamMember/slice'

const Table = lazy(() => import('../Components/TeamRoster/Table'))

// Main Component
const TeamRoster = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  
  const dispatch = useAppDispatch()
  const searchQuery = useAppSelector(selectSearchQuery)
  const loading = useAppSelector(selectLoadingTeamMembers)
  const error = useAppSelector(selectErrorTeamMembers)
  const teamMembers = useAppSelector(selectTeamMembers)
  const activeFilterCount = useAppSelector(selectActiveFilterCount)

  // Memoized fetch function to prevent unnecessary re-renders
  const fetchAllTeamMembers = useCallback(async () => {
    try {
      await dispatch(fetchTeamMembers())
    } catch (error) {
      console.error('Failed to fetch team members:', error)
    }
  }, [dispatch])

  // Initial data fetch
  useEffect(() => {
    if (!teamMembers || teamMembers.length === 0) {
      fetchAllTeamMembers()
    }
  }, [fetchAllTeamMembers, teamMembers])

  // Optimized refresh handler with loading state
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true)
    try {
      await fetchAllTeamMembers()
    } finally {
      setIsRefreshing(false)
    }
  }, [fetchAllTeamMembers])

  // Memoized search handlers
  const handleSearchChange = useCallback((query: string) => {
    dispatch(setSearchQuery(query))
  }, [dispatch])

  const handleClearSearch = useCallback(() => {
    dispatch(setSearchQuery(''))
  }, [dispatch])

  // Memoized filter handlers
  const handleFilterOpen = useCallback(() => setIsFilterOpen(true), [])
  const handleFilterClose = useCallback(() => setIsFilterOpen(false), [])

  // Memoized refresh button (always visible)
  const refreshButton = useMemo(() => (
    <Button 
      id='refresh-button' 
      color='success' 
      size='small' 
      onClick={handleRefresh}
      disabled={loading || isRefreshing}
      startIcon={isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
      sx={{ minWidth: 'auto' }}
    >
      Refresh
    </Button>
  ), [handleRefresh, loading, isRefreshing])


  return (
    <Box
      component='div'
      sx={{ 
        p: 0, 
        backgroundColor: backgroundColors.white, 
        width: '100%',
        maxWidth: '100vw',
        minHeight: '400px',
        position: 'relative',
        overflow: 'hidden'
      }}
      borderRadius={2}
      boxShadow={1}
    >
      {/* Header with actions */}
      <Box
        sx={{ 
          p: 2, 
          borderBottom: '1px solid #e0e0e0',
          position: 'sticky',
          top: 0,
          backgroundColor: backgroundColors.white,
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: { xs: 'flex-end', sm: 'space-between' },
          flexWrap: 'nowrap'
        }}
      >
        <Box sx={{ display: { xs: 'none', sm: 'block' }, flexShrink: 0, whiteSpace: 'nowrap' }}>
          <Upload />
        </Box>
        
        {/* iPad specific layout */}
        <Box sx={{ 
          display: { xs: 'none', sm: 'none', md: 'flex', lg: 'none' },
          alignItems: 'center',
          gap: 1,
          justifyContent: 'flex-start',
          ml: 2
        }}>
          <>
            {refreshButton}
            <Badge badgeContent={activeFilterCount} color='error' overlap='rectangular'>
              <Button
                id='filter-button'
                color='success'
                startIcon={<FilterListIcon />}
                onClick={handleFilterOpen}
                size='small'
                disabled={loading}
                sx={{ minWidth: 'auto' }}
              >
                Filter
              </Button>
            </Badge>
            <Box sx={{ minWidth: 200 }}>
              <SearchInput
                searchQuery={searchQuery}
                onSearchChange={handleSearchChange}
                onClearSearch={handleClearSearch}
              />
            </Box>
          </>
        </Box>

        {/* Mobile and Desktop layout */}
        <Box sx={{ 
          display: { xs: 'flex', sm: 'flex', md: 'none', lg: 'flex' },
          justifyContent: 'flex-end', 
          width: { xs: '100%', sm: 'auto' },
          gap: 1,
          flexShrink: 0
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'row', sm: 'row' },
            gap: 1,
            alignItems: 'center',
            justifyContent: 'flex-end',
            width: '100%'
          }}>
            {/* Mobile layout: Search bar with buttons on the same row */}
            <Box sx={{ 
              display: { xs: 'flex', sm: 'none' },
              width: '100%',
              alignItems: 'center',
              gap: 0.5,
              justifyContent: 'flex-end'
            }}>
              <Badge badgeContent={activeFilterCount} color='error' overlap='rectangular'>
                <Button
                  id='filter-button'
                  color='success'
                  startIcon={<FilterListIcon />}
                  onClick={handleFilterOpen}
                  size='small'
                  disabled={loading}
                  sx={{ minWidth: 'auto', flexShrink: 0 }}
                >
                  Filter
                </Button>
              </Badge>
              <Box sx={{ 
                '& .MuiFormControl-root': {
                  margin: 0,
                  width: '140px !important'
                }
              }}>
                <SearchInput
                  searchQuery={searchQuery}
                  onSearchChange={handleSearchChange}
                  onClearSearch={handleClearSearch}
                />
              </Box>
            </Box>
            
            {/* Desktop layout: integrated row */}
            <Box sx={{
              display: { xs: 'none', sm: 'flex' },
              flexDirection: 'row',
              gap: 1,
              alignItems: 'center',
              justifyContent: 'flex-end'
            }}>
              {refreshButton}
              <Badge badgeContent={activeFilterCount} color='error' overlap='rectangular'>
                <Button
                  id='filter-button'
                  color='success'
                  startIcon={<FilterListIcon />}
                  onClick={handleFilterOpen}
                  size='small'
                  disabled={loading}
                  sx={{ minWidth: 'auto' }}
                >
                  Filter
                </Button>
              </Badge>
              {/* Search input for desktop only */}
              <Box sx={{ minWidth: 250 }}>
                <SearchInput
                  searchQuery={searchQuery}
                  onSearchChange={handleSearchChange}
                  onClearSearch={handleClearSearch}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Error state */}
      {error && (
        <Box sx={{ p: 2 }}>
          <Alert severity="error" onClose={() => window.location.reload()}>
            {error}. Click to retry.
          </Alert>
        </Box>
      )}

      {/* Main content */}
      <Box sx={{ position: 'relative' }}>
        <LastUpdateInfo show={true} />
        <Suspense fallback={
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300, padding: 3 }}>
            <CircularProgress size={40} />
          </Box>
        }>
          <Table />
        </Suspense>

        {/* Loading overlay for initial load */}
        {loading && (!teamMembers || teamMembers.length === 0) && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              zIndex: 2,
              minHeight: 300
            }}
          >
            <CircularProgress />
          </Box>
        )}
      </Box>

      {/* Filter modal */}
      <TeamRosterFilter open={isFilterOpen} onClose={handleFilterClose} />
    </Box>
  )
}

export default TeamRoster
